import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFilterData, IFilterTagData } from '@pages/Products/Products';
import { DateFilters } from '../filter/filterSlice';

export interface IUpdatesOptions {
  manufactorNames: IFilterData[];
  tags: IFilterTagData[];
  startDate?: Date | null;
  endDate?: Date | null;
  sort?: string;
  direction?: 'ASC' | 'DESC' | undefined;
  checkThirtyDays: boolean;
  checkSixtyDays: boolean;
  checkNinetyDays: boolean;
}

export const updatesSlice = createSlice({
  name: 'updates',
  initialState: {
    options: {
      manufactorNames: [],
      tags: [],
      startDate: null,
      endDate: null,
      sort: undefined,
      direction: undefined,
      checkThirtyDays: false,
      checkSixtyDays: false,
      checkNinetyDays: false,
      loadingUpdateId: null,
    } as IUpdatesOptions,
    activeTab: 'new' as 'new' | 'prioritized' | 'completed' | 'dismissed',
    loadingUpdateId: [] as string[],
    /** Filter updatedAt, a date */
    updatedAt: null as string | null,
  },
  reducers: {
    addUpdatesLoadingUpdateId: (state, action: PayloadAction<string>) => {
      state.loadingUpdateId.push(action.payload);
    },
    removeUpdatesLoadingUpdateId: (state, action: PayloadAction<string>) => {
      state.loadingUpdateId = state.loadingUpdateId.filter(
        item => item !== action.payload,
      );
    },
    updateUpdatesManufactorNames: (
      state,
      action: PayloadAction<IFilterData[]>,
    ) => {
      state.options.manufactorNames = action.payload;
    },
    updateUpdatesTags: (state, action: PayloadAction<IFilterTagData[]>) => {
      state.options.tags = action.payload;
    },
    updateUpdatesThirtyDays: (state, action: PayloadAction<boolean>) => {
      state.options.checkThirtyDays = action.payload;
    },
    updateUpdatesSixtyDays: (state, action: PayloadAction<boolean>) => {
      state.options.checkSixtyDays = action.payload;
    },
    updateUpdatesNinetyDays: (state, action: PayloadAction<boolean>) => {
      state.options.checkNinetyDays = action.payload;
    },
    updateUpdatesStartDate: (state, action: PayloadAction<Date | null>) => {
      state.options.startDate = action.payload;
    },
    updateUpdatesEndDate: (state, action: PayloadAction<Date | null>) => {
      state.options.endDate = action.payload;
    },
    updateSort: (state, action: PayloadAction<string | undefined>) => {
      state.options.sort = action.payload;
    },
    updateSortDirection: (
      state,
      action: PayloadAction<'ASC' | 'DESC' | undefined>,
    ) => {
      state.options.direction = action.payload;
    },
    updateUpdatedAt: (state, action: PayloadAction<string | null>) => {
      state.updatedAt = action.payload;
    },
    updateUpdatesFilterDate: (state, action: PayloadAction<DateFilters>) => {
      state.options.checkThirtyDays = false;
      state.options.checkSixtyDays = false;
      state.options.checkNinetyDays = false;
      state.options.startDate = action.payload.startDate;
      state.options.endDate = action.payload.endDate;
    },
    clearUpdates: state => {
      state.options.manufactorNames = [];
      state.options.tags = [];
      state.options.startDate = null;
      state.options.endDate = null;
      state.options.sort = undefined;
      state.options.direction = undefined;
      state.options.checkThirtyDays = false;
      state.options.checkSixtyDays = false;
      state.options.checkNinetyDays = false;
    },
  },
});

export const {
  updateUpdatesManufactorNames,
  updateUpdatesTags,
  updateUpdatesStartDate,
  updateUpdatesEndDate,
  clearUpdates,
  updateSort,
  updateSortDirection,
  updateUpdatesThirtyDays,
  updateUpdatesSixtyDays,
  updateUpdatesNinetyDays,
  addUpdatesLoadingUpdateId,
  removeUpdatesLoadingUpdateId,
  updateUpdatesFilterDate,
  updateUpdatedAt,
} = updatesSlice.actions;

export const { reducer: updatesReducer } = updatesSlice;
